import iconMappingJson from './icon-mapping.json';

/**
 * Per-app icon metadata. Mirrors the legacy
 * `wf-audit/src/slices/common/assets/icon-mapping.json` shape — each
 * entry pairs the Fusion `data_app` slug with a 32/64/128/256-sized
 * CDN icon URL + a hex `theme` (no leading `#`) + the human-readable
 * `alt` text used for tooltips.
 */
export interface IconMappingEntry {
  alt: string;
  data_app: string;
  src32: string;
  src64: string;
  src128: string;
  src256: string;
  theme: string;
}

interface ResolvedIcon {
  /** Human-readable name for tooltip + alt text. */
  alt: string;
  /** Fusion CDN URL — empty string if unresolved (caller falls back to a letter chip). */
  src: string;
  /** Hex theme background, no leading `#`. */
  theme: string;
  /** True when we hit the mapping. False when we fell back to a colour-hash chip. */
  matched: boolean;
}

const MAPPING = iconMappingJson as ReadonlyArray<IconMappingEntry>;

const BY_TYPE = new Map<string, IconMappingEntry>();
const BY_ALT = new Map<string, IconMappingEntry>();
for (const entry of MAPPING) {
  if (entry.data_app) BY_TYPE.set(entry.data_app, entry);
  if (entry.alt) BY_ALT.set(normalize(entry.alt), entry);
}

/**
 * `Adobe Workfront` → `adobeworkfront`. Strips spaces, punctuation,
 * and parentheses so labels like `Adobe Workfront (sandbox-01)` still
 * match the `alt` index.
 */
function normalize(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/**
 * Deterministic palette for un-mapped apps so a column of unknown
 * `applications[].type` slugs doesn't show as a grey blur — same slug
 * always picks the same colour.
 */
const FALLBACK_PALETTE = [
  '0ea5e9', // sky-500
  '14b8a6', // teal-500
  '22c55e', // green-500
  'a855f7', // purple-500
  'ec4899', // pink-500
  'ef4444', // red-500
  'f97316', // orange-500
  'eab308', // yellow-500
  '64748b', // slate-500
  '6366f1', // indigo-500
];

function hashStringToIndex(s: string, modulo: number): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) {
    h = (h * 31 + s.charCodeAt(i)) | 0;
  }
  return Math.abs(h) % modulo;
}

const warned = new Set<string>();

/**
 * Resolve an engine application to its visual metadata.
 *
 * Match strategies, in order:
 *   1. `type` exact against `data_app`
 *   2. Doubled slug — `workfront` → `workfront-workfront` (Fusion's
 *      legacy `data_app` keys frequently double the prefix)
 *   3. Normalized `label` against the legacy `alt` index (strips
 *      spaces + punctuation, so `Adobe Workfront (sandbox-01)`
 *      matches `Adobe Workfront`)
 *   4. Prefix match: any `alt` whose normalized form is a prefix of
 *      the normalized label (catches `Adobe Photoshop Lightroom` →
 *      `Adobe Photoshop`)
 *   5. Colour-hash chip + first letter of label as the visual hint
 *
 * Dev-mode emits a one-time `console.warn` per unresolved
 * `type|label` pair so unknown slugs surface in the browser console
 * without spamming on every render — paste those into the chat and
 * we can extend the mapping or add another match strategy.
 */
export function resolveIcon(type: string | null | undefined, label?: string | null): ResolvedIcon {
  if (type) {
    const byType = BY_TYPE.get(type);
    if (byType) return toResolved(byType);
    // Fusion's `data_app` keys frequently use the app slug doubled
    // (e.g., `workfront-workfront`). Try the doubled variant when the
    // single form isn't in the index.
    const doubled = `${type}-${type}`;
    const byDoubled = BY_TYPE.get(doubled);
    if (byDoubled) return toResolved(byDoubled);
  }
  if (label) {
    const norm = normalize(label);
    const byAlt = BY_ALT.get(norm);
    if (byAlt) return toResolved(byAlt);
    for (const [altKey, entry] of BY_ALT) {
      if (norm.startsWith(altKey) || altKey.startsWith(norm)) {
        return toResolved(entry);
      }
    }
  }
  if (import.meta.env.DEV) {
    const key = `${type ?? ''}|${label ?? ''}`;
    if (!warned.has(key)) {
      warned.add(key);
      // eslint-disable-next-line no-console
      console.warn('[icon-resolver] unresolved app', { type, label });
    }
  }
  const seed = `${label ?? ''}|${type ?? ''}` || '?';
  const theme = FALLBACK_PALETTE[hashStringToIndex(seed, FALLBACK_PALETTE.length)]!;
  return { alt: label ?? type ?? 'App', src: '', theme, matched: false };
}

function toResolved(entry: IconMappingEntry): ResolvedIcon {
  return { alt: entry.alt, src: entry.src64, theme: entry.theme, matched: true };
}
